import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Award, Users, Target, Lightbulb, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { honorService, pageContentService } from '@/services/storage';
import type { Honor, PageContent } from '@/types';

const milestones = [
  { year: '2017', title: '公司缘起', description: '瀚昕微团队组建，开始芯片设计研发' },
  { year: '2019', title: '正式成立', description: '在无锡注册成立瀚昕微电子（无锡）有限公司' },
  { year: '2020', title: '产品量产', description: '首批产品成功量产并投放市场' },
  { year: '2021', title: '战略投资', description: '获得TCL资本、小米产投战略投资' },
  { year: '2022', title: '快速发展', description: '产品线扩展，覆盖更多应用领域' },
  { year: '2023', title: '国际认证', description: '产品通过USB-IF等国际认证' },
  { year: '2024', title: '持续创新', description: '持续推出创新产品，服务更多客户' },
];

export default function About() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('company');
  const [honors, setHonors] = useState<Honor[]>([]);
  const [currentHonorIndex, setCurrentHonorIndex] = useState(0);
  const [pageContent, setPageContent] = useState<PageContent>({} as PageContent);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['company', 'philosophy', 'honors'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    const loadData = async () => {
      setHonors(await honorService.getAll());
      setPageContent(await pageContentService.get());
    };
    loadData();
  }, []);

  // Auto-rotate honors carousel
  useEffect(() => {
    if (honors.length === 0) return;
    const interval = setInterval(() => {
      setCurrentHonorIndex((prev) => (prev + 1) % honors.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [honors.length]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  const nextHonor = () => {
    setCurrentHonorIndex((prev) => (prev + 1) % honors.length);
  };

  const prevHonor = () => {
    setCurrentHonorIndex((prev) => (prev - 1 + honors.length) % honors.length);
  };

  const philosophy = pageContent.philosophy || { items: [] };
  const about = pageContent.about || { content: [] };

  return (
    <div className="pt-20">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              关于瀚昕微
            </h1>
            <p className="text-xl text-blue-100">
              专注于高性能、高品质数模混合芯片研发
            </p>
          </div>
        </div>
      </section>

      {/* Tabs Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="w-full justify-start mb-8">
              <TabsTrigger value="company">公司简介</TabsTrigger>
              <TabsTrigger value="philosophy">经营理念</TabsTrigger>
              <TabsTrigger value="honors">荣誉资质</TabsTrigger>
            </TabsList>

            {/* Company Profile */}
            <TabsContent value="company">
              <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    {about.title || '公司简介'}
                  </h2>
                  <div 
                    className="space-y-4 text-gray-600"
                    style={{ 
                      fontSize: `${about.fontSize || 16}px`,
                      lineHeight: about.lineHeight || 1.8 
                    }}
                  >
                    {(about.content || []).map((paragraph: string, index: number) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>
                <div className="relative">
                  <div className="aspect-video rounded-xl overflow-hidden">
                    {about.video ? (
                      <video
                        src={about.video}
                        controls
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={about.image || 'https://images.unsplash.com/photo-1565514020176-db9af97c5a5c?w=800&q=80'}
                        alt="Company"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="mt-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-8">发展历程</h3>
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-blue-200" />
                  <div className="space-y-8">
                    {milestones.map((milestone, index) => (
                      <div key={index} className="relative pl-10">
                        <div className="absolute left-0 top-1 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {index + 1}
                        </div>
                        <div className="border-l-2 border-blue-200 pl-4 pb-2">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-bold text-lg text-gray-900">{milestone.title}</h4>
                            <span className="text-blue-600 font-bold">{milestone.year}</span>
                          </div>
                          <p className="text-gray-600">{milestone.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Philosophy */}
            <TabsContent value="philosophy">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {philosophy.title || '经营理念'}
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  我们始终坚持客户至上、技术领先、品质卓越的经营理念
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                {(philosophy.items || []).map((item: any, index: number) => (
                  <div 
                    key={index} 
                    className="relative overflow-hidden rounded-xl aspect-[3/4] group"
                  >
                    <img
                      src={item.image || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&q=80'}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                        {index === 0 ? <Target className="h-6 w-6 text-white" /> :
                         index === 1 ? <Lightbulb className="h-6 w-6 text-white" /> :
                         index === 2 ? <Award className="h-6 w-6 text-white" /> :
                         <Users className="h-6 w-6 text-white" />}
                      </div>
                      <h3 
                        className="text-2xl font-bold mb-2"
                        style={{ color: item.textColor || '#ffffff' }}
                      >
                        {item.title}
                      </h3>
                      <p 
                        style={{ 
                          color: item.textColor || '#ffffff',
                          fontSize: `${item.fontSize || 16}px`
                        }}
                      >
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">我们的使命</h3>
                <p className="text-lg text-gray-600 mb-4">
                  为客户提供高性能、高品质的电源管理芯片解决方案，助力中国半导体产业发展。
                </p>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 mt-8">我们的愿景</h3>
                <p className="text-lg text-gray-600">
                  成为全球领先的电源管理芯片设计公司，让每一台电子设备都能使用瀚昕微的芯片。
                </p>
              </div>
            </TabsContent>

            {/* Honors - Carousel */}
            <TabsContent value="honors">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">荣誉资质</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  我们的努力得到了行业和社会的认可
                </p>
              </div>

              {honors.length > 0 ? (
                <div className="relative max-w-4xl mx-auto">
                  {/* Carousel */}
                  <div className="relative aspect-video bg-gray-100 rounded-xl overflow-hidden">
                    {honors.map((honor, index) => (
                      <div
                        key={honor.id}
                        className={`absolute inset-0 transition-opacity duration-500 ${
                          index === currentHonorIndex ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        {honor.image ? (
                          <img
                            src={honor.image}
                            alt={honor.title}
                            className="w-full h-full object-contain bg-gray-50"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
                            <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                              <Award className="h-12 w-12 text-yellow-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900">{honor.title}</h3>
                            {honor.year && (
                              <p className="text-gray-500 mt-2">获得年份：{honor.year}</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Navigation */}
                  <button
                    onClick={prevHonor}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={nextHonor}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>

                  {/* Indicators */}
                  <div className="flex justify-center mt-4 space-x-2">
                    {honors.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentHonorIndex(index)}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          index === currentHonorIndex ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Honor List */}
                  <div className="mt-8 grid md:grid-cols-2 gap-4">
                    {honors.map((honor, index) => (
                      <Card 
                        key={honor.id} 
                        className={`cursor-pointer transition-all ${
                          index === currentHonorIndex ? 'ring-2 ring-blue-500' : ''
                        }`}
                        onClick={() => setCurrentHonorIndex(index)}
                      >
                        <CardContent className="p-4 flex items-center space-x-4">
                          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Award className="h-6 w-6 text-yellow-600" />
                          </div>
                          <div>
                            <h3 className="font-bold">{honor.title}</h3>
                            {honor.year && <p className="text-sm text-gray-500">{honor.year}</p>}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  暂无荣誉资质数据
                </div>
              )}

              <div className="mt-12 bg-blue-50 rounded-xl p-8">
                <h3 className="text-xl font-bold text-blue-900 mb-4">质量体系认证</h3>
                <p className="text-blue-700">
                  公司已通过ISO9001质量管理体系认证，建立了完善的质量管理体系，从设计、生产到售后服务的全过程进行严格的质量控制，确保产品的高品质。
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
